/**
 * LocationInputComponent for React Native
 * Based on LIC API specification with React Native adjustments
 */

import type { LocationType } from "../__generated__/types.gqlgenerated";
import type {
  Gen2Location,
  LocationSuggestion,
} from "./util/fetchLocationAutocompleteSuggestionsViaGraphql";

import { AutoComplete } from "./components/AutoComplete";
import { BottomSheetAutoComplete } from "./components/BottomSheetAutoComplete";
import { useModalRef } from "./components/ContentModal";
import { useLocationAutocomplete } from "./hooks/useLocationAutocomplete";
import { useValidator } from "./hooks/useValidator";
import { SuggestionsAnnouncement } from "../shared/components/SuggestionsAnnouncement";
import { useI18n } from "../shared/I18n";

import React, { type ReactNode, useCallback, useEffect, useState, useMemo, useRef } from "react";
import { StyleSheet, type ViewStyle } from "react-native";

// Export types and components
export type {
  LocationSuggestion,
  Gen2Location,
} from "./util/fetchLocationAutocompleteSuggestionsViaGraphql";
export { BottomSheetModalProvider } from "./components/BottomSheetModalProvider";

/**
 * Configuration for the autocomplete service
 */
export interface LocationAutocompleteUserOptions {
  /** Country code for autocomplete results (e.g., 'US') */
  country: string;

  /** Language code for autocomplete results (e.g., 'en') */
  language: string;

  /** Filter results by location type */
  locationTypeFilter?: LocationType[];

  /** Use client IP address for location context */
  useIp?: boolean;

  /** Maximum number of autocomplete suggestions (default: 8) */
  maxResults?: number;
}

/**
 * Result object returned via onValueSelected callback
 */
export interface LocationInputComponentResult {
  /** Validation status of the selected location */
  isValid: boolean;

  /** Gen2 location format (React Native uses Gen2 instead of NamedLocation) */
  location?: Gen2Location;

  /** Formatted display string for the location */
  formattedName?: string;
}

/**
 * Error codes for programmatic handling
 */
export enum LocationInputErrorCode {
  NETWORK_ERROR = "NETWORK_ERROR", // Network/service failure
  NOTFOUND_ERROR = "NOTFOUND_ERROR", // Location not recognized
  SELECTION_ERROR = "SELECTION_ERROR", // User must select from suggestions
}

/**
 * Error object returned via onLocationError callback
 */
export interface LocationInputError {
  /** User-facing error message */
  errorMessage: string;

  /** Error type for programmatic handling */
  errorCode: LocationInputErrorCode;
}

/**
 * Additional logging data provided with callbacks
 */
export interface LocationInputLogData {
  /** Number of characters typed by user */
  numCharacters?: number;

  /** Number of autocomplete suggestions returned */
  numSuggestions?: number;

  /** Whether escape hatch was used (may be dropped for RN) */
  isEscapeHatch?: boolean;
}

/**
 * Configuration for custom tags displayed alongside locations
 */
export interface CustomTagConfig {
  /** Tag label text */
  label: string;

  /** Optional icon (IFL icon component) */
  icon?: React.ReactElement;

  /** Determines if tag should be displayed for a location */
  shouldRender: (location: Gen2Location) => boolean;

  /** Tag appearance props (variant, status, etc.) */
  status?: "info" | "success" | "warning" | "critical";

  /** Additional tag props */
  variant?: string;
}

/**
 * Flat location object for prefilling the component
 * Contains human-readable location fields without IDs
 * Can be constructed from device detection or profile data
 */
export interface PrefillLocation {
  /** Latitude coordinate */
  latitude?: number;

  /** Longitude coordinate */
  longitude?: number;

  /** Street address (e.g., "123 Main St") */
  streetAddress?: string;

  /** City name (e.g., "Austin") */
  city?: string;

  /** First-level administrative division (e.g., "TX" for Texas, "ON" for Ontario) */
  admin1?: string;

  /** Second-level administrative division (e.g., county in US, prefecture in JP) */
  admin2?: string;

  /** Third-level administrative division (used in some markets) */
  admin3?: string;

  /** Fourth-level administrative division (used in some markets) */
  admin4?: string;

  /** Postal/ZIP code (e.g., "78701") */
  postalCode?: string;

  /**
   * Server-formatted non-address portion of the location (e.g., "Austin, TX
   * 78701", "東京都千代田区"). Locale-aware, so prefer this over locally
   * concatenating city/admin1/postalCode when present.
   */
  fullFormattedLocation?: string;
}

/**
 * Helper function to build a display string from PrefillLocation or string
 *
 * Prefers the server-formatted `fullFormattedLocation` when present
 * (locale-aware), falling back to a manual `streetAddress, city, admin1
 * postalCode` join. The fallback only works correctly for Western locales
 * — populate `fullFormattedLocation` from the server whenever possible.
 */
function buildStringPrefill(prefill: string | PrefillLocation | undefined): string {
  if (!prefill) return "";

  if (typeof prefill === "string") {
    return prefill;
  }

  if (prefill.fullFormattedLocation) {
    return prefill.streetAddress
      ? `${prefill.streetAddress}, ${prefill.fullFormattedLocation}`
      : prefill.fullFormattedLocation;
  }

  // Fallback: manual join. Western-locale order.
  const parts: string[] = [];

  if (prefill.streetAddress) {
    parts.push(prefill.streetAddress);
  }

  if (prefill.city) {
    parts.push(prefill.city);
  }

  if (prefill.admin1) {
    parts.push(prefill.admin1);
  }

  if (prefill.postalCode) {
    // Add postal code to the last part without a comma
    if (parts.length > 0) {
      parts[parts.length - 1] = `${parts[parts.length - 1]} ${prefill.postalCode}`;
    } else {
      parts.push(prefill.postalCode);
    }
  }

  return parts.join(", ");
}

export interface LocationInputComponentProps {
  // Required Props
  /** Configuration for the autocomplete service */
  options: LocationAutocompleteUserOptions;

  /** Locale used for error message translations (e.g., 'en_US') */
  locale: string;

  /** Label for the form field. Omit to render the input without a built-in label. */
  label?: ReactNode;

  /** Callback fired when a location is selected or input loses focus */
  onValueSelected: (item: LocationInputComponentResult, logData?: LocationInputLogData) => void;

  // Optional Props
  /** Pre-populate the input with a location */
  prefill?: string | PrefillLocation;

  /** Indicates if the string prefill is a precise location. Must be set correctly for string prefills to avoid validation failures for city-level locations. */
  preciseLocation?: boolean;

  /** If true, treats empty input as invalid */
  required?: boolean;

  /** Callback fired when an error occurs */
  onLocationError?: (error?: LocationInputError, logData?: LocationInputLogData) => void;

  /** Accessibility label for the input field */
  ariaLabel?: string;

  /** Helper text displayed below the input field */
  helperText?: string;

  /** Session ID for tracking multiple components on the same page */
  pageSessionId?: string;

  /** Name of the client page containing the component */
  clientPage?: string;

  /** Props to pass to suggestion items (currently supports data-dd prefixed attributes) */
  suggestionItemInnerProps?: (index: number) => Record<string, any>;

  /** Configuration for custom tags displayed alongside locations */
  customTags?: CustomTagConfig[];

  // Modal variant props
  /** Use modal variant instead of inline autocomplete */
  useModal?: boolean;

  /** ID for the modal (required when useModal is true) */
  modalId?: string;

  /** Whether to dismiss modal on selection (only for modal variant) */
  dismissOnSelect?: boolean;

  /** Test ID for testing */
  testID?: string;

  /** Input field style */
  inputStyle?: ViewStyle;

  /**
   * Style overrides for the autocomplete suggestions container. Merged on
   * top of the default options-wrapper styles. Lets callers absolute
   * position the dropdown so it overlays surrounding layout (e.g. when
   * LIC is inside a fixed-size modal and the dropdown would otherwise
   * push content below it).
   */
  optionsWrapperStyle?: ViewStyle;

  /** Whether structured location validation is enabled*/
  useStructuredValidation?: boolean;

  /** Placeholder text shown in the input field. */
  placeholder?: string;
}

/**
 * LocationInputComponentCore - Internal implementation with all logic
 * Provides location suggestions with Gen2 location data
 */
export function LocationInputComponentCore({
  // Required props
  options,
  locale: _locale, // Will be used for error message translations
  label,
  onValueSelected,
  // Optional props
  prefill = "",
  preciseLocation: _preciseLocation = true, // Will be used to validate prefill precision
  required = false,
  onLocationError: _onLocationError,
  ariaLabel,
  helperText,
  pageSessionId: _pageSessionId, // Will be used for logging/analytics
  clientPage: _clientPage, // Will be used for logging/analytics
  suggestionItemInnerProps: _suggestionItemInnerProps, // Will be used for data-dd attributes
  customTags: _customTags, // Will be used for displaying tags alongside locations
  // Modal variant options
  useModal = false,
  modalId = "location-modal",
  dismissOnSelect = true,
  testID,
  inputStyle,
  optionsWrapperStyle,
  useStructuredValidation = false,
  placeholder,
}: LocationInputComponentProps) {
  const initialValue = buildStringPrefill(prefill);
  const [value, setValue] = useState(initialValue);
  const [autoCompleteValue, setAutoCompleteValue] = useState(initialValue);
  const [isSelecting, setIsSelecting] = useState(!!initialValue);
  const modalRef = useModalRef();
  const justSelectedRef = useRef(false);
  const i18n = useI18n();
  const resolvedPlaceholder = placeholder ?? i18n.gettext("Enter location");
  // Tracks whether the user has typed anything. Guards the validator effect from
  // overriding autoCompleteValue/isSelecting after the user has already started
  // typing — a race that occurs on iOS when the validator response arrives while
  // the user is already interacting (the formSheet animation delays rendering).
  const userHasTypedRef = useRef(false);

  // Memoize prefill by its primitive fields
  const prefillAsString = typeof prefill === "string" ? prefill : undefined;
  const prefillStreetAddress = typeof prefill === "string" ? undefined : prefill?.streetAddress;
  const prefillCity = typeof prefill === "string" ? undefined : prefill?.city;
  const prefillAdmin1 = typeof prefill === "string" ? undefined : prefill?.admin1;
  const prefillAdmin2 = typeof prefill === "string" ? undefined : prefill?.admin2;
  const prefillAdmin3 = typeof prefill === "string" ? undefined : prefill?.admin3;
  const prefillAdmin4 = typeof prefill === "string" ? undefined : prefill?.admin4;
  const prefillPostalCode = typeof prefill === "string" ? undefined : prefill?.postalCode;
  const prefillFullFormattedLocation =
    typeof prefill === "string" ? undefined : prefill?.fullFormattedLocation;
  const prefillLatitude = typeof prefill === "string" ? undefined : prefill?.latitude;
  const prefillLongitude = typeof prefill === "string" ? undefined : prefill?.longitude;
  const stablePrefill = useMemo(
    () => prefill,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      prefillAsString,
      prefillStreetAddress,
      prefillCity,
      prefillAdmin1,
      prefillAdmin2,
      prefillAdmin3,
      prefillAdmin4,
      prefillPostalCode,
      prefillFullFormattedLocation,
      prefillLatitude,
      prefillLongitude,
    ],
  );

  // Validate prefill using useValidator hook
  // - Test group (useStructuredValidation=true): Pass PrefillLocation → uses ValidateLocationData
  // - Control group (useStructuredValidation=false): Convert PrefillLocation to string → uses ValidateStringLocation
  // - Legacy string prefills: Pass as-is → uses ValidateStringLocation

  let prefillForValidation: string | PrefillLocation | undefined;
  let preciseLocationForValidation: boolean;

  if (!stablePrefill) {
    prefillForValidation = undefined;
    preciseLocationForValidation = _preciseLocation;
  } else if (typeof stablePrefill === "string") {
    // String prefill - use as-is (legacy)
    prefillForValidation = stablePrefill;
    preciseLocationForValidation = _preciseLocation;
  } else if (useStructuredValidation) {
    // Test group - pass PrefillLocation object
    prefillForValidation = stablePrefill;
    preciseLocationForValidation = !!stablePrefill.streetAddress;
  } else {
    // Control group - convert PrefillLocation to string for string validation
    prefillForValidation = buildStringPrefill(stablePrefill);
    preciseLocationForValidation = !!stablePrefill.streetAddress;
  }

  const { result: validationResult, loading: validateLoading } = useValidator(
    prefillForValidation,
    {
      country: options.country,
      language: options.language,
      preciseLocation: preciseLocationForValidation,
    },
  );

  useEffect(() => {
    const newValue = buildStringPrefill(stablePrefill);
    setValue(newValue);
    setAutoCompleteValue(newValue);
    userHasTypedRef.current = false;
  }, [stablePrefill]);

  useEffect(() => {
    if (!stablePrefill || validateLoading) {
      return;
    }

    // Don't overwrite what the user is actively typing — the validator response
    // may arrive late on iOS due to the formSheet animation delaying initial render.
    if (userHasTypedRef.current) {
      return;
    }

    if (validationResult.isValid && validationResult.formattedName) {
      // Update display to the backend-normalized name.
      setValue(validationResult.formattedName);
      setAutoCompleteValue(validationResult.formattedName);
      setIsSelecting(true);
    } else if (!validationResult.isValid) {
      setValue("");
      setAutoCompleteValue("");
      onValueSelected({ isValid: false });
    }
    // Intentionally omitting 'onValueSelected' from deps: it's a parent-provided callback that may
    // not be wrapped in useCallback, so including it would cause this effect to re-run on every
    // parent render. This effect should only re-run when validation state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stablePrefill, validateLoading, validationResult]);

  // Fetch location suggestions via GraphQL
  const { suggestions, fullSuggestions } = useLocationAutocomplete(
    autoCompleteValue,
    !isSelecting,
    options.country,
    options.language,
    500, // debounce ms
    options.maxResults,
    options.locationTypeFilter,
  );

  const displaySuggestions = useMemo(
    () => (isSelecting ? [] : suggestions),
    [isSelecting, suggestions],
  );

  const handleOnInput = useCallback(
    (newText: string) => {
      // Block input if we just made a selection
      // This prevents native keyboard autocomplete from interfering with our selection
      // and causing the dropdown to reappear after selecting a location
      if (justSelectedRef.current) {
        return;
      }

      userHasTypedRef.current = true;
      setAutoCompleteValue(newText);

      // If clearing the field (empty string), also clear the main value
      // This ensures the modal trigger input reflects the cleared state
      if (newText === "") {
        setValue("");
        setIsSelecting(true); // Stop showing suggestions

        // Also notify parent that the value was cleared
        onValueSelected(
          {
            isValid: false,
            formattedName: "",
            location: undefined,
          },
          undefined,
        );
      } else {
        // Reset isSelecting if user is typing
        setIsSelecting(false);
      }

      // TODO: Handle required field validation
      // TODO: Use suggestionItemInnerProps for data-dd attributes on suggestions
      // TODO: Apply customTags to show tags alongside location suggestions
    },
    [onValueSelected],
  );

  const handleOnChangeText = useCallback(
    (text: string) => {
      // Block input events briefly when selection is made
      justSelectedRef.current = true;
      setTimeout(() => {
        justSelectedRef.current = false;
      }, 100);

      // Check if this is a selection from suggestions
      const matchingLocation = fullSuggestions.find(
        (loc: LocationSuggestion) => loc.suggestion === text,
      );

      if (matchingLocation && matchingLocation.gen2Location) {
        setValue(text);
        setAutoCompleteValue(text);
        setIsSelecting(true);

        // TODO: Clear any existing errors via onLocationError(undefined)
        // TODO: Add LocationInputLogData with numCharacters, numSuggestions

        // Call onValueSelected with the successful selection
        onValueSelected(
          {
            isValid: true,
            location: matchingLocation.gen2Location,
            formattedName: text,
          },
          undefined,
        );

        // Dismiss modal if in modal mode
        if (useModal && dismissOnSelect && modalRef.current) {
          modalRef.current?.dismiss();
        }
      }
    },
    [fullSuggestions, onValueSelected, useModal, dismissOnSelect, modalRef],
  );

  const handleOnBlur = useCallback(() => {
    // Check if there are active suggestions that the user might be selecting
    if (suggestions.length > 0 || justSelectedRef.current) {
      return;
    }

    setIsSelecting(true);

    // Check if field is required and empty
    if (required && !value) {
      if (_onLocationError) {
        _onLocationError({
          errorMessage: "This field is required",
          errorCode: LocationInputErrorCode.SELECTION_ERROR,
        });
      }

      onValueSelected({ isValid: false });
      return;
    }

    // Check if the current value matches a suggestion
    const matchingLocation = fullSuggestions.find(
      (loc: LocationSuggestion) => loc.suggestion === autoCompleteValue,
    );

    if (!matchingLocation && autoCompleteValue !== value) {
      // Revert to last valid value if no match (no free text support)
      setAutoCompleteValue(value);

      // Report selection error if text doesn't match
      if (_onLocationError && autoCompleteValue) {
        _onLocationError({
          errorMessage: "Please select from the suggestions",
          errorCode: LocationInputErrorCode.SELECTION_ERROR,
        });
      }
    } else if (matchingLocation) {
      // If it's a valid match, update the value to match
      setValue(autoCompleteValue);
    }
  }, [
    autoCompleteValue,
    value,
    fullSuggestions,
    suggestions,
    required,
    _onLocationError,
    onValueSelected,
  ]);

  // TODO: Set error message based on validation state (needs i18n with locale prop)
  const errorMessage = null; // For now, no error display

  // When modal opens, ensure autoCompleteValue matches the current valid value
  const handleModalOpen = useCallback(() => {
    setAutoCompleteValue(value);
  }, [value]);

  // Memoize the AutoComplete component. In modal mode the helperText is
  // rendered above the trigger by BottomSheetAutoComplete, so we omit it
  // from the inner-modal AutoComplete to avoid duplication.
  const autoCompleteContent = useMemo(() => {
    return (
      <>
        <AutoComplete
          label={typeof label === "string" ? label : undefined}
          helperText={useModal ? undefined : helperText}
          value={autoCompleteValue}
          onInput={handleOnInput}
          onSelect={handleOnChangeText}
          options={displaySuggestions}
          loading={false}
          onBlur={handleOnBlur}
          clearable={true}
          focusOnMount={false}
          placeholder={resolvedPlaceholder}
          optionsWrapperStyle={
            optionsWrapperStyle
              ? { ...styles.optionsTable, ...optionsWrapperStyle }
              : styles.optionsTable
          }
          inputWrapperStyle={inputStyle}
          aria-label={ariaLabel}
          testID={testID}
        />
        <SuggestionsAnnouncement suggestions={displaySuggestions} />
      </>
    );
  }, [
    label,
    helperText,
    useModal,
    autoCompleteValue,
    handleOnInput,
    handleOnChangeText,
    displaySuggestions,
    handleOnBlur,
    ariaLabel,
    testID,
    resolvedPlaceholder,
    optionsWrapperStyle,
    inputStyle,
  ]);

  // Modal variant
  if (useModal) {
    return (
      <BottomSheetAutoComplete
        id={modalId}
        value={value}
        onChangeText={handleOnChangeText}
        clearable={true}
        testID={testID}
        placeholder={resolvedPlaceholder}
        errorMessage={errorMessage}
        inputStyle={inputStyle}
        modalRef={modalRef}
        onModalOpen={handleModalOpen}
        ariaLabel={ariaLabel}
        label={label}
        helperText={helperText}
      >
        {autoCompleteContent}
      </BottomSheetAutoComplete>
    );
  }

  // Inline variant (default)
  return autoCompleteContent;
}

const styles = StyleSheet.create({
  optionsTable: {
    borderWidth: 0,
  },
});

// No default export - this component is internal only
